import type { TransactionApiItem, TransactionViewLine } from '@/api/transaction-views/queries';
import {
  formatSignedAmount,
  formatSignedLineAmount,
  formatTransactionStatus,
} from '@/features/transactions/utils/transaction-detail';
import {
  formatTransactionDate,
  formatTransactionDirection,
  formatTransactionUsdValue,
} from '@/features/transactions/utils/transaction-list';

import { CoinIcon } from '../crypto-assets/coin-icon';
import {
  DirectionLabel,
  StatusLabel,
  Summary,
  SummaryAmount,
  SummaryContent,
  SummaryDate,
  SummaryMeta,
  SummarySeparator,
  SummaryUsd,
  SummaryValue,
} from './transaction-detail-modal.styles';

export const TransactionDetailSummary = ({ detail }: { detail: TransactionApiItem }) => (
  <Summary role="group" aria-label="Transaction summary">
    <SummaryContent>
      <SummaryMeta>
        <DirectionLabel as="span" variant="body4" fontWeight={600}>
          {formatTransactionDirection(detail.direction).toUpperCase()}
        </DirectionLabel>
        {detail.onchain?.status ? (
          <>
            <SummarySeparator as="span" variant="body4" aria-hidden="true">
              ·
            </SummarySeparator>
            <StatusLabel as="span" variant="body4" fontWeight={600} $status={detail.onchain.status}>
              {formatTransactionStatus(detail.onchain.status)}
            </StatusLabel>
          </>
        ) : null}
      </SummaryMeta>
      {detail.lines.map((line, index) => (
        <TransactionSummaryLine key={index} line={line} showsCoin={detail.onchain !== null} />
      ))}
      <SummaryDate variant="body2">{formatTransactionDate(detail.date)}</SummaryDate>
    </SummaryContent>
  </Summary>
);

const TransactionSummaryLine = ({
  line,
  showsCoin,
}: {
  line: TransactionViewLine;
  showsCoin: boolean;
}) => {
  const amountInUsd = formatTransactionUsdValue(line.amountInUsd);
  return (
    <SummaryAmount role="group" aria-label="Transaction amount">
      <SummaryValue variant="h4" fontWeight={600} aria-label={formatSignedLineAmount(line)}>
        <span>{formatSignedAmount(line.amount)}</span>
        {showsCoin && line.denomination ? (
          <CoinIcon symbol={line.denomination} iconUrl={line.iconUrl} size={28} />
        ) : null}
        {line.denomination ? <span>{line.denomination}</span> : null}
      </SummaryValue>
      {amountInUsd ? (
        <SummaryUsd as="span" variant="body2">
          ≈ {amountInUsd}
        </SummaryUsd>
      ) : null}
    </SummaryAmount>
  );
};
